import type { Map as MLMap } from "maplibre-gl";

import { useSelectionStore, type Selection } from "../state/useSelectionStore";

/**
 * Fly to a point and select whatever zone the map lands on.
 *
 * The selection itself is left to the map's own click handler — fired here as
 * a synthetic click once the camera has arrived. Building a Selection by hand
 * would mean duplicating what the handler reads off the tiles (price
 * attributes, the green cell, the air and noise classes) and getting a
 * half-populated panel wherever the duplicate fell behind.
 *
 * The wait is `moveend` and then `idle`, not one or the other. `idle` alone
 * can fire from the state the camera was already in — the map was sitting
 * still when the jump was asked for — and the click then lands on wherever the
 * map used to be, selecting the place you were leaving. `moveend` says the
 * camera has arrived; the `idle` after it says the tiles underneath have
 * drawn, which is the first moment `queryRenderedFeatures` can see anything.
 *
 * Returns a cancel, because a second jump started before the first lands must
 * be able to drop it — otherwise you arrive at the place you changed your mind
 * about a moment after arriving at the one you wanted.
 */
export function jumpTo(
  map: MLMap,
  {
    lon,
    lat,
    zoom,
    onArrive,
  }: {
    lon: number;
    lat: number;
    zoom: number;
    /** Adjust the selection the map made, e.g. to attach a searched address. */
    onArrive?: (selection: Selection) => Selection;
  },
): () => void {
  const arrived = () => {
    map.fire("click", {
      point: map.project([lon, lat]),
      lngLat: { lng: lon, lat },
    } as never);
    const { selection, select } = useSelectionStore.getState();
    if (selection && onArrive) select(onArrive(selection));
  };
  const settled = () => map.once("idle", arrived);

  // Registered before the camera moves, so the first `moveend` seen belongs to
  // this jump.
  map.once("moveend", settled);
  map.flyTo({ center: [lon, lat], zoom, duration: 700 });

  return () => {
    map.off("moveend", settled);
    map.off("idle", arrived);
  };
}
